<script>
	import client from "src/services/client";
	import { navigate } from "svelte-routing";
	import { Col, Row } from "sveltestrap";
	import { damageReviewedData, errorMessage } from "src/store/store";
	import { onMount } from "svelte";

	import ConfirmModal from "../../../../components/CustomModal/ConfirmModal.svelte";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import CustomCollapseCard from "../../../../components/CustomCollapseCard/CustomCollapseCard.svelte";
	import ErrorMessageHandler from "../../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import LoaderContainer from "src/components/Loader/LoaderContainer.svelte";
	import PageHeader from "../../../../components/PageHeader/PageHeader.svelte";
	import ReportCard from "../../../../components/ReportCard/ReportCard.svelte";
	import { convert } from "../../../../services/helpers";
	import {
		extractErrorMessage,
		splitCamelCaseAndCapitalize,
	} from "../../../../utils/misc";
	import { loading } from "../../../../store/store";

	//#region Props
	export let id;
	//#endregion

	//#region Variables
	let err;
	let data = {};
	let lastReviewed;
	let beforeLastReviewed;
	let header = "";
	let reportText = "";
	let isOpen = false;
	let assetOwner = "";

	let reportOptions = [];
	let page = null;
	let totalPage = null;
	let selectedLastReviewed = null;
	let selectedBeforeLastReviewed = null;
	let sortDir = "DESC";
	let size = 10;
	let hasLocalData = false;
	let reportedDamage = [];

	let showDropdown = false;
	let modalSubmitHandler;

	errorMessage.subscribe((value) => {
		err = value;
	});
	//#endregion

	//#region API Calls
	/**
	 * Fetches paginated report options for a given vehicleId.
	 * If reset is true, resets pagination and clears current options.
	 * Maps API response to option objects for dropdowns.
	 * Ensures no duplicate options are added.
	 * Updates totalPage and increments page for next fetch.
	 *
	 * @param {string|number} vehicleId - The ID of the vehicle to fetch reports for.
	 * @param {boolean} [reset=false] - Whether to reset pagination and clear current options.
	 */
	async function fetchReportOptions(vehicleId, reset = false) {
		try {
			if (reset) {
				page = 1;
				reportOptions = [];
				totalPage = 1;
			}

			const res = await client(
				`/v1/CaseManagement/vsrReports?vehicleId=${vehicleId}`,
				{ method: "GET", version: "Tasking" }
			);

			const list = res?.data?.data ?? [];

			const newOptions = list.map((item, idx) => ({
				value: idx,
				label: `${
					item.isLastReviewed ? "(Last Reviewed) " : ""
				}Report ${item.count.split("#")[0]} – ${
					item.bookingNo
				} ${splitCamelCaseAndCapitalize(item.type)}`,
				bookingId: item.bookingId,
				caseNo: item.bookingNo,
				type: item.type,
			}));

			// const existingValues = new Set(
			// 	reportOptions.map((opt) => opt.value)
			// );
			// const uniqueOptions = newOptions.filter(
			// 	(opt) => !existingValues.has(opt.value)
			// );

			reportOptions = newOptions;
			selectedLastReviewed = reportOptions.find(
				(opt) =>
					opt.bookingId === lastReviewed?.bookingId &&
					opt.type === lastReviewed?.reportType
			);
			selectedBeforeLastReviewed = reportOptions.find(
				(opt) =>
					opt.bookingId === beforeLastReviewed?.bookingId &&
					opt.type === beforeLastReviewed?.reportType
			);

			//Only update totalPage from backend
			totalPage = res?.data?.data?.totalPage ?? 1;

			//Increment page only if current page fetch was successful
			page++;
		} catch (err) {
			console.error("Failed to fetch report options:", err);
		}
	}

	/**
	 * Handles selection of the "last reviewed" report.
	 *
	 * Checks localStorage for cached report data. If found, uses local data; otherwise, fetches from API.
	 * Updates lastReviewed and selectedLastReviewed variables.
	 *
	 * @param {object} option - The selected report option object.
	 */
	async function handleLastReviewedChange(option) {
		try {
			const key = `caseInvestigate${data.vehicleId}`;
			const stored = JSON.parse(localStorage.getItem(key) || "[]");

			const local = stored.find((item) => item.caseNo === option.value);
			if (local) {
				lastReviewed = local;
				selectedLastReviewed = option;
				return;
			}

			const res = await client(
				`/v1/CaseManagement/vsrReport/booking/${option.bookingId}?vehicleId=${data.vehicleId}&type=${option.type}`,
				{ method: "GET", version: "Tasking" }
			);
			if (res?.data?.bookingImages) {
				res.data.images = res.data.bookingImages;
				delete res.data.bookingImages;
			}
			lastReviewed = res?.data;
			lastReviewed.reportType = res.data.type || res.data.reportType;
			delete lastReviewed.type;
			const existingIndex = $damageReviewedData.findIndex(
				(item) => item.bookingId === lastReviewed.bookingId
			);
			if (existingIndex !== -1) {
				lastReviewed = $damageReviewedData[existingIndex];
			}
			selectedLastReviewed = option;
		} catch (e) {
			console.error("Failed to load last reviewed report", e);
		}
	}

	/**
	 * Handles selection of the "before last reviewed" report.
	 * Checks localStorage for cached report data.
	 * If found, uses local data; otherwise, fetches from API.
	 * Updates beforeLastReviewed and selectedBeforeLastReviewed variables.
	 *
	 * @param {object} option - The selected report option object.
	 */
	async function handleBeforeLastReviewedChange(option) {
		try {
			const key = `caseInvestigate${data.vehicleId}`;
			const stored = JSON.parse(localStorage.getItem(key) || "[]");

			const local = stored.find((item) => item.caseNo === option.value);
			if (local) {
				beforeLastReviewed = local;
				selectedBeforeLastReviewed = option;
				return;
			}

			const res = await client(
				`/v1/CaseManagement/vsrReport/booking/${option.bookingId}?vehicleId=${data.vehicleId}&type=${option.type}`,
				{ method: "GET", version: "Tasking" }
			);
			if (res?.data?.bookingImages) {
				res.data.images = res.data.bookingImages;
				delete res.data.bookingImages;
			}
			beforeLastReviewed = res?.data;
			beforeLastReviewed.reportType =
				res.data.type || res.data.reportType;
			delete beforeLastReviewed.type;
			const existingIndex = $damageReviewedData.findIndex(
				(item) => item.bookingId === beforeLastReviewed.bookingId
			);
			if (existingIndex !== -1) {
				beforeLastReviewed = $damageReviewedData[existingIndex];
			}
			selectedBeforeLastReviewed = option;
		} catch (e) {
			console.error("Failed to load before last reviewed report", e);
		}
	}

	/**
	 * Fetches investigation review data by the provided id.
	 * Sets the data variable with the response.
	 * If vehicleId exists, fetches report options for the vehicle.
	 * Handles and logs errors.
	 *
	 * @param {void}
	 */
	async function fetchReviewbyId() {
		try {
			const res = await client(`/v1/CaseManagement/report/${id}`, {
				method: "GET",
				version: "Tasking",
			});

			data = res?.data;
			lastReviewed = data?.lastReviewed;
			// selectedLastReviewed = "Report-0";
			// selectedBeforeLastReviewed = "Report-1";
			beforeLastReviewed = data?.beforeLastReviewed;

			if (data?.vehicleId) {
				await fetchReportOptions(data.vehicleId, true);
			}
			return;
		} catch (error) {
			console.error(error);
			extractErrorMessage(error);
		}
	}

	/**
	 * Fetches the asset owner name for a given vehicleId.
	 * Makes an API call to retrieve vehicle details and sets the assetOwner variable.
	 *
	 *
	 * @param {string|number} vehicleId - The ID of the vehicle to fetch the asset owner for.
	 */
	async function fetchAssetOwner(vehicleId) {
		try {
			const res = await client(`/v1/${vehicleId}/details`, {
				method: "GET",
				version: "Vehicle",
			});
			assetOwner = res.data.assetOwnerName;
		} catch (error) {
			console.error(error);
			extractErrorMessage(error);
		}
	}

	//#endregion

	//#region View Functions
	/**
	 * Closes the modal.
	 * Navigates to the damage validation page for the current vehicle and closes the modal.
	 *
	 * @param {void}
	 */
	function handleSubmit() {
		if (data?.vehicleId) {
			isOpen = false; // close the modal
			navigate(
				`/issues/case-management/investigate-damage-validation?vehicleId=${data.vehicleId}&id=${id}`
			);
		}
	}

	async function handleReject(reason) {
		try {
			const res = await client(`/v1/CaseManagement/report/${id}`, {
				method: "PUT",
				version: "Tasking",
				data: {
					status: "Rejected",
					reason,
				},
			});
			isOpen = false;
			navigate("/issues/case-management?tab=4");
		} catch (err) {
			console.log(err);
			extractErrorMessage(err);
		}
	}

	/**
	 * Toggles the visibility of the confirmation modal and sets its content.
	 *
	 * @param {string} headerText - The text to display as the modal's header.
	 * @param {string} text - The main content or message to display in the modal.
	 * @param {boolean} withDropdown - Whether to show a dropdown in the modal.
	 * @param {function} submitHandler - The function to call when the modal's confirm button is clicked.
	 */
	function toggleModal(headerText, text, withDropdown, submitHandler) {
		isOpen = true;
		header = headerText;
		reportText = text;
		showDropdown = withDropdown;
		modalSubmitHandler = submitHandler;
	}

	/**
	 * Checks if there is any local investigation data stored for the current vehicle.
	 * Returns true if localStorage contains any entries for the vehicle, otherwise false.
	 *
	 * @returns {boolean} - Whether local data exists for the current vehicle.
	 */
	function checkLocalData() {
		if (!data?.vehicleId) return false;
		const key = `damageReviewedData`;
		const stored = JSON.parse(localStorage.getItem(key) || "[]");
		return stored.length > 0;
	}

	/*
		onMount lifecycle hook:
		- Sets loading state to true
		- Fetches investigation review data by ID
		- If vehicleId exists, fetches report options for the vehicle
		- Sets the selected reports for "last reviewed" and "before last reviewed" if available
		- Handles errors and resets loading state when done
	*/
	onMount(async () => {
		loading.set(true);
		try {
			await fetchReviewbyId();
			if (data?.vehicleId) {
				await fetchAssetOwner(data.vehicleId);
			}
		} catch (e) {
			error = e;
			console.error("Failed to load data:", e);
		} finally {
			loading.set(false);
			hasLocalData = checkLocalData();
		}
	});
</script>

<LoaderContainer>
	<Row class="max-height">
		<Col xs={12}>
			<div>
				<ErrorMessageHandler message={err} />
				<Col style="margin-bottom: 24px;">
					<PageHeader
						title={data.plateNo}
						titleStatusBar="Under Investigation"
					/>
				</Col>

				<!-- #region Vehicle Information -->
				<div class="mb-4">
					<CustomCollapseCard
						title="Vehicle Information"
						isOpenDefault={true}
					>
						<div class="p-0">
							<div class="row">
								<div class="col">
									<div class="description">
										Vehicle Number
									</div>
									<div class="value">{data?.plateNo}</div>
								</div>
								<div class="col">
									<div class="description">Assets Owner</div>
									<div class="value">
										{assetOwner}
									</div>
								</div>
							</div>

							<hr class="divider" />

							<div class="row">
								<div class="col">
									<div class="description">Make</div>
									<div class="value">
										{data?.vehicleInfo?.make}
									</div>
								</div>
								<div class="col">
									<div class="description">Model</div>
									<div class="value">
										{data?.vehicleInfo?.model}
									</div>
								</div>
							</div>
						</div>
					</CustomCollapseCard>
				</div>
				<!-- #endregion -->

				<!-- #region Damage Information -->
				<div class="mb-5">
					<CustomCollapseCard
						title="Reviewer Information"
						isOpenDefault={true}
					>
						<div class="p-0">
							<div class="row">
								<div class="col">
									<div class="description">Reviewer Name</div>
									<div class="value">
										{data?.lastReviewedBy}
									</div>
								</div>
								<div class="col">
									<div class="description">
										Last Reviewed Date
									</div>
									<div class="value">
										{convert(data?.lastReviewedAt)}
									</div>
								</div>
							</div>
						</div>
					</CustomCollapseCard>
				</div>
				<!-- #endregion -->

				<!-- #region Damage Investigation Card -->
				<div class="d-flex flex-row max-height report-container">
					<Col
						xs={6}
						style="padding-right: 12px; position: relative;"
					>
						{#if lastReviewed?.bookingId === data?.lastReviewed?.bookingId}
							<div class="last-reviewed-badge">LAST REVIEWED</div>
						{/if}

						{#if lastReviewed && reportOptions.length > 0}
							<ReportCard
								data={{
									vehicleId: data.vehicleId,
									plateNo: data.plateNo,
									...lastReviewed,
								}}
								options={reportOptions}
								value={selectedLastReviewed}
								onSelectReport={handleLastReviewedChange}
								onLoadMore={() => {
									if (page <= totalPage)
										fetchReportOptions(data.vehicleId);
								}}
								lightbox={true}
								caseManagement={true}
								on:localDataUpdated={() => {
									hasLocalData = checkLocalData(); // update the reactive state
								}}
							/>
						{/if}
					</Col>
					<Col xs={6} style="padding-left: 12px;">
						<!-- {#if beforeLastReviewed && reportOptions.length > 0} -->
						<ReportCard
							data={{
								vehicleId: data.vehicleId,
								plateNo: data.plateNo,
								...beforeLastReviewed,
							}}
							options={reportOptions}
							value={selectedBeforeLastReviewed}
							onSelectReport={handleBeforeLastReviewedChange}
							onLoadMore={() => {
								if (page <= totalPage)
									fetchReportOptions(data.vehicleId);
							}}
							lightbox={true}
							caseManagement={true}
							on:localDataUpdated={() => {
								hasLocalData = checkLocalData(); // update the reactive state
							}}
						/>
						<!-- {/if} -->
					</Col>
				</div>
				<!-- #endregion -->

				<!-- #region Action Buttons -->
				<!-- Reject button -->
				<div class="d-flex flex-row justify-content-end my-1">
					<div class="mx-1">
						<CustomButton
							buttonText="Reject"
							onClick={() =>
								toggleModal(
									"Reject Report",
									"You are about to declare that this vehicle do not have any new damages as of the last report. Please indicate a reason.",
									true,
									handleReject
								)}
						/>
					</div>

					<!-- Submit button -->
					<div class="mx-1">
						<CustomButton
							buttonText="Submit Investigation"
							color="custom-blue"
							disabled={$damageReviewedData.length === 0}
							onClick={() =>
								toggleModal(
									"Submit Investigations",
									"You are about to submit these investigations and proceed to the quotation phase. Are you sure you want to proceed?",
									false, // no dropdown for submit investigation modal
									handleSubmit
								)}
						/>
					</div>
				</div>
				<!-- #endregion -->
			</div>
		</Col>
	</Row>
</LoaderContainer>
<ConfirmModal
	open={isOpen}
	toggle={() => (isOpen = false)}
	{header}
	bodyText={reportText}
	buttonColor="primary"
	buttonText="Confirm"
	outline={false}
	handleSubmit={modalSubmitHandler}
	{showDropdown}
	dropdownOptions={[
		{ value: "NoNewDamage", label: "No new damage" },
		{ value: "NoDamage", label: "No damage" },
	]}
/>

<style>
	:global(.max-height) {
		height: fit-content;
	}

	:global(.divider) {
		border: 0.5px solid var(--fds-on-surface-variant);
		margin: 24px 0;
	}

	.description {
		color: var(--fds-on-surface-variant);
	}

	.report-container {
		margin-bottom: 24px;
	}

	.last-reviewed-badge {
		position: absolute;
		top: -16px;
		left: 0;
		background-color: #248a3d; /* green */
		color: white;
		font-weight: 700;
		font-size: 16px;
		padding: 5px 18px;
		border-radius: 4px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		z-index: 1;
	}
</style>
